/**
 * Recover the reason an `<img>`/`<video>` source failed to load.
 *
 * Those elements report failure with a bare `error` event — no status, no body
 * — so the only way to learn why is to request the same URL again and read the
 * proxy's JSON. The reason→message mapping itself lives in
 * `@polygraf/shared/proxy-errors` (`proxyErrorTranslation`).
 */

import {
	classifyProxyResponse,
	getNativeToken,
	PROXY_PROBE_TIMEOUT_MS,
	type ProxyFetchOutcome,
	resolveFetchCredentials,
	withNativeBearer,
} from '@polygraf/shared'

/**
 * Ask the proxy why a media element failed.
 *
 * Runs only on the failure path, and only for URLs on the API origin: a native
 * app fetching a LAN source directly has no proxy to ask. `ok` means the retry
 * succeeded, so the element failed and not the source. `unknown` means no
 * recognisable proxy error — the caller then keeps its generic message rather
 * than rendering a server string.
 */
export async function probeProxyError(
	url: string,
	apiUrl: string,
	signal?: AbortSignal,
): Promise<ProxyFetchOutcome> {
	const unknown: ProxyFetchOutcome = { kind: 'unknown' }
	if (signal?.aborted) return unknown
	// Origin AND path: `?url=` accepts any absolute address, so an https source
	// pointing at our own API would otherwise make this re-request an arbitrary
	// endpoint with credentials. Only the proxy routes answer in this shape.
	let target: URL
	try {
		target = new URL(url, window.location.href)
	} catch {
		return unknown
	}
	if (target.origin !== new URL(apiUrl).origin) return unknown
	if (!target.pathname.startsWith('/api/proxy/')) return unknown

	// Own controller so a success can be cut off immediately: the URL may be an
	// endless MJPG stream, and leaving that body undrained would hold a
	// connection open for the life of the page.
	const controller = new AbortController()
	const abort = () => controller.abort()
	signal?.addEventListener('abort', abort, { once: true })
	// Bounded so a viewer holding its error message for this answer is not held
	// for good; long enough for the proxy's slowest refusal (see the constant).
	const timer = setTimeout(abort, PROXY_PROBE_TIMEOUT_MS)
	try {
		// In the app WebView there is no cookie: probe with the native bearer, as
		// the frame fetch did, so a refusal reports the real reason. Never follow
		// a redirect with it.
		const headers = withNativeBearer(url, apiUrl, getNativeToken())
		const response = await fetch(url, {
			headers,
			credentials: resolveFetchCredentials(url, apiUrl),
			redirect: headers.has('Authorization') ? 'error' : 'follow',
			signal: controller.signal,
		})
		if (response.ok) {
			controller.abort()
			return { kind: 'ok' }
		}
		return classifyProxyResponse({
			ok: false,
			status: response.status,
			body: await response.json().catch(() => null),
			retryAfter: response.headers.get('Retry-After'),
		})
	} catch {
		return unknown
	} finally {
		clearTimeout(timer)
		signal?.removeEventListener('abort', abort)
	}
}
